/**
 * Configuration Index and Validation
 * Following constitution requirements for configuration management
 *
 * This file provides centralized access to all configuration
 * and handles validation and initialization.
 */

import { getAppConfig, APP_CONFIG, APP_CONSTANTS } from './app';
import { getEnvironmentConfig, validateEnvironmentConfig, initializeEnvironment } from './environment';
import { API_ENDPOINTS, HTTP_STATUS, createApiClient } from './api';
import { ROUTES, routeUtils, routeBuilders, DEFAULT_REDIRECTS } from './routes';
import { initializeFeatures, validateFeatureFlags, featureUtils } from './features';
import {
  ERROR_TYPES,
  HTTP_STATUS_CODES,
  USER_ROLES,
  SUBSCRIPTION_STATUS,
  MARGIN_TYPES,
  COEFFICIENT_TYPES,
  BOOKMAKERS,
  STORAGE_KEYS,
  EVENTS,
  VALIDATION_RULES
} from '../constants';

/**
 * Configuration validation results
 */
let configValidation = {
  isValid: false,
  errors: [],
  warnings: [],
  timestamp: null
};

/**
 * Initialize all application configuration
 * @returns {Object} Complete application configuration
 */
export function initializeConfiguration() {
  const errors = [];
  const warnings = [];

  try {
    // Initialize environment configuration
    const envConfig = initializeEnvironment();

    // Validate environment configuration
    const envValidation = validateEnvironmentConfig(envConfig);
    if (!envValidation.isValid) {
      errors.push.apply(errors, envValidation.errors);
    }
    warnings.push.apply(warnings, envValidation.warnings);

    // Initialize feature flags
    const features = initializeFeatures();

    // Validate feature flags
    const featureValidation = validateFeatureFlags();
    if (!featureValidation.isValid) {
      errors.push.apply(errors, featureValidation.errors);
    }
    warnings.push.apply(warnings, featureValidation.warnings);

    // Get complete app configuration
    const appConfig = getAppConfig();

    // Validate critical configuration values
    validateCriticalConfig(appConfig, errors, warnings);

    // Update validation results
    configValidation = {
      isValid: errors.length === 0,
      errors: errors,
      warnings: warnings,
      timestamp: new Date().toISOString()
    };

    if (!configValidation.isValid) {
      console.error('Configuration validation failed:', configValidation.errors);
      throw new Error('Configuration validation failed: ' + configValidation.errors.join(', '));
    }

    if (configValidation.warnings.length > 0) {
      console.warn('Configuration warnings:', configValidation.warnings);
    }

    // Log successful initialization
    if (envConfig.debug.enabled) {
      console.log('✅ Configuration initialized successfully', {
        environment: envConfig.nodeEnv,
        features: Object.keys(features).filter(function(key) { return features[key]; }).length,
        apiEndpoints: Object.keys(API_ENDPOINTS).length,
        routes: Object.keys(ROUTES).length,
        timestamp: configValidation.timestamp
      });
    }

    return {
      app: appConfig,
      environment: envConfig,
      features: features,
      api: API_ENDPOINTS,
      routes: ROUTES,
      constants: {
        errorTypes: ERROR_TYPES,
        httpStatus: HTTP_STATUS_CODES,
        userRoles: USER_ROLES,
        subscriptionStatus: SUBSCRIPTION_STATUS,
        marginTypes: MARGIN_TYPES,
        coefficientTypes: COEFFICIENT_TYPES,
        bookmakers: BOOKMAKERS,
        storageKeys: STORAGE_KEYS,
        events: EVENTS,
        validationRules: VALIDATION_RULES
      },
      validation: configValidation
    };

  } catch (error) {
    console.error('Configuration initialization failed:', error);

    // Set validation to failed state
    configValidation = {
      isValid: false,
      errors: [error.message],
      warnings: warnings,
      timestamp: new Date().toISOString()
    };

    throw error;
  }
}

/**
 * Validate critical configuration values
 * @param {Object} config - Application configuration
 * @param {Array} errors - Errors array to populate
 * @param {Array} warnings - Warnings array to populate
 */
function validateCriticalConfig(config, errors, warnings) {
  // Validate JWT token expiry
  if (!config.session || !config.session.tokenExpiryMinutes) {
    errors.push('JWT token expiry time not configured');
  } else if (config.session.tokenExpiryMinutes !== 30) {
    warnings.push('JWT token expiry is not set to 30 minutes as per constitution');
  }

  // Validate auto-refresh interval
  if (!config.dataRefresh || !config.dataRefresh.surebetsAutoRefreshMs) {
    errors.push('Surebet auto-refresh interval not configured');
  } else if (config.dataRefresh.surebetsAutoRefreshMs !== 600000) {
    warnings.push('Auto-refresh interval is not set to 10 minutes as per constitution');
  }

  // Validate default language
  if (!config.defaultLanguage || config.defaultLanguage !== 'bg') {
    warnings.push('Default language should be Bulgarian (bg) as per constitution');
  }

  // Validate primary color
  if (!config.ui || !config.ui.primaryColor || config.ui.primaryColor !== '#53d22d') {
    warnings.push('Primary color should be #53d22d as per constitution');
  }

  // Validate stake limits
  if (!config.validation || config.validation.stakeMinAmount < 0) {
    errors.push('Minimum stake amount must be non-negative');
  }

  if (config.validation && config.validation.stakeMinAmount >= config.validation.stakeMaxAmount) {
    errors.push('Minimum stake must be less than maximum stake');
  }
}

/**
 * Get configuration status
 * @returns {Object} Configuration validation status
 */
export function getConfigurationStatus() {
  return Object.assign({}, configValidation);
}

/**
 * Re-validate configuration
 * @returns {Object} Updated validation results
 */
export function revalidateConfiguration() {
  try {
    return initializeConfiguration();
  } catch (error) {
    return {
      isValid: false,
      error: error.message,
      timestamp: new Date().toISOString()
    };
  }
}

/**
 * Configuration exports for easy access
 */
export const config = {
  // Core configuration
  app: APP_CONFIG,
  constants: APP_CONSTANTS,

  // API configuration
  api: {
    endpoints: API_ENDPOINTS,
    status: HTTP_STATUS,
    client: createApiClient
  },

  // Route configuration
  routes: {
    definitions: ROUTES,
    utils: routeUtils,
    builders: routeBuilders,
    defaults: DEFAULT_REDIRECTS
  },

  // Feature management
  features: {
    utils: featureUtils,
    validate: validateFeatureFlags,
    initialize: initializeFeatures
  },

  // Environment management
  environment: {
    get: getEnvironmentConfig,
    validate: validateEnvironmentConfig,
    initialize: initializeEnvironment
  },

  // Configuration management
  management: {
    initialize: initializeConfiguration,
    validate: revalidateConfiguration,
    getStatus: getConfigurationStatus
  }
};

/**
 * Quick access to commonly used configuration
 */
export const quickConfig = {
  // API base URL
  apiBaseURL: function() {
    return getEnvironmentConfig().api.baseURL;
  },

  // Default language
  defaultLanguage: function() {
    return getAppConfig().defaultLanguage;
  },

  // Is development mode
  isDevelopment: function() {
    return process.env.NODE_ENV === 'development';
  },

  // Auto-refresh interval
  autoRefreshInterval: function() {
    return getAppConfig().dataRefresh.surebetsAutoRefreshMs;
  },

  // Primary color
  primaryColor: function() {
    return getAppConfig().ui.primaryColor;
  }
};

export default config;
